import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PeerReviewWorkflow = ({ caseId, onReviewSubmit, onApprovalRequest }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [reviewComment, setReviewComment] = useState('');
  const [selectedReviewer, setSelectedReviewer] = useState('');

  // Mock peer review data
  const mockReviews = {
    pending: [
      {
        id: 'review_1',
        caseTitle: 'Climate Change Data Verification',
        requester: 'Dr. Elena Rossi',
        requestedAt: new Date(Date.now() - 3600000),
        priority: 'high',
        deadline: new Date(Date.now() + 86400000),
        type: 'primary_review',
        status: 'awaiting_review',
        methodology: 'European Code of Standards',
        estimatedTime: '45 minutes'
      },
      {
        id: 'review_2',
        caseTitle: 'Social Media Misinformation Analysis',
        requester: 'Marcus Weber',
        requestedAt: new Date(Date.now() - 7200000),
        priority: 'medium',
        deadline: new Date(Date.now() + 172800000),
        type: 'secondary_review',
        status: 'awaiting_review',
        methodology: 'EFCSN Standards',
        estimatedTime: '30 minutes'
      }
    ],
    inProgress: [
      {
        id: 'review_3',
        caseTitle: 'Economic Statistics Fact-Check',
        requester: 'Dr. Sarah Johnson',
        reviewer: 'You',
        startedAt: new Date(Date.now() - 1800000),
        priority: 'critical',
        deadline: new Date(Date.now() + 43200000),
        type: 'expert_review',
        status: 'in_progress',
        progress: 65,
        methodology: 'Statistical Verification Protocol'
      }
    ],
    completed: [
      {
        id: 'review_4',
        caseTitle: 'Healthcare Misinformation Assessment',
        requester: 'Dr. Michael Chen',
        reviewer: 'You',
        completedAt: new Date(Date.now() - 86400000),
        verdict: 'approved_with_changes',
        confidence: 85,
        type: 'primary_review',
        feedback: `Comprehensive analysis with strong evidence base. Recommend minor adjustments to confidence scoring methodology and additional expert consultation for medical claims.`,
        methodology: 'Medical Fact-Check Protocol'
      },
      {
        id: 'review_5',
        caseTitle: 'Political Statement Verification',
        requester: 'Anna Kowalski',
        reviewer: 'Dr. Elena Rossi',
        completedAt: new Date(Date.now() - 172800000),
        verdict: 'approved',
        confidence: 92,
        type: 'secondary_review',
        feedback: 'Excellent methodology application and thorough source verification. Ready for publication.',
        methodology: 'Political Fact-Check Standards'
      }
    ]
  };

  // Mock available reviewers
  const availableReviewers = [
    {
      id: 'reviewer_1',
      name: 'Dr. Elena Rossi',
      expertise: ['Climate Science', 'Environmental Policy'],
      availability: 'available',
      workload: 3,
      rating: 4.8,
      languages: ['English', 'Italian', 'German']
    },
    {
      id: 'reviewer_2',
      name: 'Marcus Weber',
      expertise: ['Social Media Analysis', 'Digital Forensics'],
      availability: 'busy',
      workload: 7,
      rating: 4.6,
      languages: ['English', 'German', 'French']
    },
    {
      id: 'reviewer_3',
      name: 'Dr. Sarah Johnson',
      expertise: ['Economics', 'Statistical Analysis'],
      availability: 'available',
      workload: 2,
      rating: 4.9,
      languages: ['English', 'Spanish']
    }
  ];

  const handleReviewAction = (reviewId, action) => {
    // Handle review actions (accept, decline, etc.)
    console.log(`Review ${reviewId}: ${action}`);
  };

  const handleSubmitReview = (reviewId) => {
    if (reviewComment?.trim()) {
      onReviewSubmit?.(reviewId, reviewComment);
      setReviewComment('');
    }
  };

  const handleRequestReview = () => {
    if (selectedReviewer) {
      onApprovalRequest?.(selectedReviewer);
      setSelectedReviewer('');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'awaiting_review': return 'text-warning';
      case 'in_progress': return 'text-primary';
      case 'approved': return 'text-success';
      case 'approved_with_changes': return 'text-warning';
      case 'rejected': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-destructive/10 text-destructive';
      case 'high': return 'bg-warning/10 text-warning';
      case 'medium': return 'bg-primary/10 text-primary';
      case 'low': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const renderReviewCard = (review) => (
    <div key={review?.id} className="p-4 bg-card border border-border rounded-lg hover:shadow-soft transition-smooth">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-medium text-foreground mb-1">{review?.caseTitle}</h4>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <span>by {review?.requester}</span>
            <span>•</span>
            <span>{review?.methodology}</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(review?.priority)}`}>
            {review?.priority}
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium bg-muted ${getStatusColor(review?.status)}`}>
            {review?.status?.replace('_', ' ')}
          </div>
        </div>
      </div>

      {/* Progress (for in-progress reviews) */}
      {review?.progress && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Progress</span>
            <span className="text-sm font-medium text-foreground">{review?.progress}%</span>
          </div>
          <div className="w-full h-2 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${review?.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Feedback (for completed reviews) */}
      {review?.feedback && (
        <div className="mb-3 p-3 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">{review?.feedback}</p>
          {review?.confidence && (
            <div className="mt-2 flex items-center space-x-2">
              <span className="text-xs text-muted-foreground">Confidence:</span>
              <span className="text-xs font-medium text-foreground">{review?.confidence}%</span>
            </div>
          )}
        </div>
      )}

      {/* Metadata */}
      <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
        <div className="flex items-center space-x-4">
          <span>Type: {review?.type?.replace('_', ' ')}</span>
          {review?.estimatedTime && <span>Est. {review?.estimatedTime}</span>}
          {review?.deadline && (
            <span className={review?.deadline < new Date() ? 'text-destructive' : ''}>
              Due: {review?.deadline?.toLocaleDateString()}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-1">
          <Icon name="Clock" size={12} />
          <span>
            {review?.completedAt ? review?.completedAt?.toLocaleTimeString() :
             review?.startedAt ? review?.startedAt?.toLocaleTimeString() :
             review?.requestedAt?.toLocaleTimeString()}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-2">
          {activeTab === 'pending' && (
            <>
              <Button
                variant="default"
                size="sm"
                iconName="Play"
                onClick={() => handleReviewAction(review?.id, 'accept')}
              >
                Start Review
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                onClick={() => handleReviewAction(review?.id, 'decline')}
              >
                Decline
              </Button>
            </>
          )}
          
          {activeTab === 'inProgress' && (
            <>
              <Button
                variant="default"
                size="sm"
                iconName="Play"
                onClick={() => handleReviewAction(review?.id, 'continue')}
              >
                Continue
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Pause"
                onClick={() => handleReviewAction(review?.id, 'pause')}
              >
                Pause
              </Button>
            </>
          )}
          
          {activeTab === 'completed' && (
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              onClick={() => handleReviewAction(review?.id, 'view')}
            >
              View Details
            </Button>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          iconName="MoreHorizontal"
          onClick={() => {
            // More options
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Users" size={20} className="text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">Peer Review Workflow</h3>
            <p className="text-sm text-muted-foreground">
              Collaborative fact-checking with EFCSN standards
            </p>
          </div>
        </div>

        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="UserPlus"
            onClick={() => {
              // Request new reviewer
            }}
          >
            Request Review
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Settings"
          >
            Settings
          </Button>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {[
          { id: 'pending', label: 'Pending', count: mockReviews?.pending?.length },
          { id: 'inProgress', label: 'In Progress', count: mockReviews?.inProgress?.length },
          { id: 'completed', label: 'Completed', count: mockReviews?.completed?.length }
        ]?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`
              flex-1 px-4 py-3 text-sm font-medium transition-smooth
              ${activeTab === tab?.id
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }
            `}
          >
            {tab?.label}
            {tab?.count > 0 && (
              <span className="ml-2 px-2 py-1 bg-muted rounded-full text-xs">
                {tab?.count}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {mockReviews?.[activeTab]?.map(renderReviewCard)}
          
          {mockReviews?.[activeTab]?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h4 className="font-medium text-foreground mb-2">No reviews {activeTab}</h4>
              <p className="text-sm text-muted-foreground">
                {activeTab === 'pending' && 'No pending reviews at the moment.'}
                {activeTab === 'inProgress' && 'No reviews currently in progress.'}
                {activeTab === 'completed' && 'No completed reviews to show.'}
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Request Review Panel */}
      <div className="border-t border-border p-4 bg-muted/20">
        <div className="space-y-3">
          <h4 className="font-medium text-foreground">Request Peer Review</h4>
          
          <div className="flex space-x-2">
            <div className="flex-1">
              <select
                value={selectedReviewer}
                onChange={(e) => setSelectedReviewer(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm"
              >
                <option value="">Select reviewer...</option>
                {availableReviewers?.map((reviewer) => (
                  <option key={reviewer?.id} value={reviewer?.id}>
                    {reviewer?.name} - {reviewer?.expertise?.join(', ')} ({reviewer?.availability})
                  </option>
                ))}
              </select>
            </div>
            
            <Button
              variant="default"
              iconName="Send"
              onClick={handleRequestReview}
              disabled={!selectedReviewer}
            >
              Request
            </Button>
          </div>

          {/* Quick Review Comment */}
          <div className="flex space-x-2">
            <div className="flex-1">
              <Input
                placeholder="Add review comment or feedback..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e?.target?.value)}
              />
            </div>
            <Button
              variant="outline"
              iconName="MessageSquare"
              onClick={() => handleSubmitReview('current')}
              disabled={!reviewComment?.trim()}
            >
              Comment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeerReviewWorkflow;